const filled = (entry) => {
    if (entry === '' || entry === 'null') {
        return false;
    }
    return true;
};

// allFilled checks if all 'root-level' properties of object are filled (not '', not 'null')
const allFilled = (obj, ignoredKeys) => {
    let keys;
    if (ignoredKeys) {
        keys = Object.keys(obj).filter(key => !ignoredKeys.includes(key));
    }
    else {
        keys = Object.keys(obj);
    }
    for (const key of keys) {
        if (!filled(obj[key])) {
            return false;
        }
    }
    return true;
};

// blank2empty will replace '' with empty recursively on SIMPLE objects
// NOTE: (1) will NOT recurse into array
//       (2) arrays must NOT have mixed types or objects;
//       (3) arrays must only contain "simple" types
//       (4) simple means:  string, number, boolean
//           i.e arrays must be "simple" as string[], number[], boolean[]
//       (5) null or undefined will be converted to EMPTY
const blank2empty = (obj, empty = '__EMPTY__', keepUndefined = false) => Object.keys(obj).reduce((acc, curr) => ({
    ...acc,
    [curr]: obj[curr] === null || obj[curr] === undefined
        ? keepUndefined
            ? obj[curr]
            : empty
        : Array.isArray(obj[curr])
            ? obj[curr].map((x) => (x === '' ? empty : x))
            : typeof obj[curr] === 'object'
                ? blank2empty(obj[curr], empty)
                : obj[curr] === ''
                    ? empty
                    : obj[curr],
}), {});

/** camelize will return the camelized word BUT it needs a separator!
 * @remarks already camelized words will be destroyed: e.g. logoInc => logoinc
 */
const camelize = (name, firstUpper = false, separator = '_') => {
    if (firstUpper) {
        return name
            .split(separator)
            .map((w) => w.toLowerCase().replace(/./, (m) => m.toUpperCase()))
            .join('');
    }
    return name
        .split(separator)
        .map((w, i) => {
        if (i > 0) {
            return w.toLowerCase().replace(/./, (m) => m.toUpperCase());
        }
        return w.toLowerCase();
    })
        .join('');
};

// cloneSimple (hierarchically) creates a copy of "simple" entries of object
// NOTE: (1) will NOT recurse into array
//       (2) arrays must NOT have mixed types or objects;
//       (3) arrays must only contain "simple" types
//       (4) simple means:  string, number, boolean
//           i.e arrays must be "simple" as string[], number[], boolean[]
const cloneSimple = (obj) => Object.keys(obj).reduce((acc, curr) => ({
    ...acc,
    [curr]: Array.isArray(obj[curr])
        ? obj[curr].slice(0)
        : typeof obj[curr] === 'object'
            ? cloneSimple(obj[curr])
            : obj[curr],
}), {});

// copySimple (hierarchically) copies all entries of object into destination
// NOTE: (1) will NOT recurse into array
//       (2) arrays must NOT have mixed types or objects;
//       (3) arrays must only contain "simple" types
//       (4) simple means:  string, number, boolean
//           i.e arrays must be "simple" as string[], number[], boolean[]
const copySimple = (destination, origin) => {
    const keys = Object.keys(destination);
    for (const key of keys) {
        const value = origin[key];
        if (Array.isArray(value)) {
            destination[key] = value.slice(0);
        }
        else if (typeof value === 'object') {
            copySimple(destination[key], value);
        }
        else {
            destination[key] = value;
        }
    }
};

// shallowCompareArrays returns true if two arrays are equal to each other
// NOTE: the type of the array entries must be "fundamental"
const shallowCompareArrays = (left, right) => {
    if (left.length !== right.length) {
        return false;
    }
    for (let i = 0; i < left.length; i++) {
        if (left[i] !== right[i]) {
            return false;
        }
    }
    return true;
};

// diffSimple (hierarchically) compares two objects and return true if they are different
//
// NOTE: (1) will NOT recurse into array
//       (2) arrays must NOT have mixed types or objects;
//       (3) arrays must only contain "simple" types
//       (4) simple means:  string, number, boolean
//           i.e arrays must be "simple" as string[], number[], boolean[]
//
// IMPORTANT:
//       (5) 'right' may have ore fields than 'left'
//
//            example:
//
//               diffSimple({a:'a'}, {a:'a', b:'b'}) === false
//
const diffSimple = (left, right) => {
    const keys = Object.keys(left);
    for (const key of keys) {
        const value = right[key];
        if (Array.isArray(value)) {
            if (!shallowCompareArrays(left[key], value)) {
                return true;
            }
        }
        else if (typeof left[key] === 'object' && left[key] !== null) {
            if (diffSimple(left[key], value)) {
                return true;
            }
        }
        else {
            if (left[key] !== value) {
                return true;
            }
        }
    }
    return false; // all equal
};

// email2key converts email address to key by removing the @ mark and dots
const email2key = (email) => {
    return email.toLowerCase().replace(/[@\.]/g, '_');
};

// empty2blank will replace empty with '' recursively on SIMPLE objects
// NOTE: (1) will NOT recurse into array
//       (2) arrays must NOT have mixed types or objects;
//       (3) arrays must only contain "simple" types
//       (4) simple means:  string, number, boolean
//           i.e arrays must be "simple" as string[], number[], boolean[]
//       (5) null or undefined will be converted to ''
const empty2blank = (obj, empty = '__EMPTY__', keepUndefined = false) => Object.keys(obj).reduce((acc, curr) => ({
    ...acc,
    [curr]: obj[curr] === null || obj[curr] === undefined
        ? keepUndefined
            ? obj[curr]
            : ''
        : Array.isArray(obj[curr])
            ? obj[curr].map((x) => (x === empty ? '' : x))
            : typeof obj[curr] === 'object'
                ? empty2blank(obj[curr], empty)
                : obj[curr] === empty
                    ? ''
                    : obj[curr],
}), {});

// getObjectKeys (hierarchically) returns all keys (and subkeys) of an object
// NOTE: will not recurse into array
const getObjectKeys = (obj, useBracesToIndicateNesting = true) => {
    const results = [];
    for (const key of Object.keys(obj)) {
        const value = obj[key];
        results.push(key);
        if (typeof value === 'object' && !Array.isArray(value)) {
            if (useBracesToIndicateNesting) {
                results.push('{');
            }
            getObjectKeys(value, useBracesToIndicateNesting).forEach(v => results.push(v));
            if (useBracesToIndicateNesting) {
                results.push('}');
            }
        }
    }
    return results;
};

const hasProp = (object, propertyName) => {
    return Object.prototype.hasOwnProperty.call(object, propertyName);
};

// this function will remove excess decimal digits by truncating the value
// NOTE: this function will round up or down
const limitNumDecimals = (val, maxDigits = 2) => {
    return Number(Number.parseFloat(String(val)).toFixed(maxDigits));
};

// Locales helps to extract localized messages an a resources object such as:
//   const resources = {
//     en: {
//       hello: 'Hello World',
//       home: {
//         title: 'Main page',
//       },
//       err: {
//         internal: 'INTERNAL_ERROR: Some problem happened, please contact us',
//       },
//     },
//     pt: {
//       hello: 'Alô Mundo',
//       home: {
//         title: 'Página principal',
//       },
//       err: {
//         internal: 'ERRO_INTERNO: Aconteceu algum problema, por favor nos contactar',
//       },
//     },
//   };
class Locales {
    // NOTE: resources is stored "byRef"
    constructor(resources, locale = 'en', country = 'us') {
        this.getLocale = () => this.locale;
        this.setLocale = (locale) => (this.locale = locale);
        this.getCountry = () => this.country;
        this.setCountry = (country) => (this.country = country);
        this.getIndexDay = () => (this.country === 'us' ? 1 : 0);
        this.getIndexMonth = () => (this.country === 'us' ? 0 : 1);
        this.getFirstFieldMax = () => (this.country === 'us' ? 12 : 31);
        this.getSecondFieldMax = () => (this.country === 'us' ? 31 : 12);
        this.getDateFormat = () => (this.country === 'us' ? 'MM/DD/YYYY' : 'DD/MM/YYYY');
        this.translate = (path, defaultMessage = '') => {
            // check for locale in resources
            if (!hasProp(this.resources, this.locale)) {
                return `INTERNAL_ERROR: cannot find locale ${this.locale} in resources object`;
            }
            // resources
            const res = this.resources[this.locale];
            // check for path
            if (!path) {
                return 'INTERNAL_ERROR: path variable must not be empty';
            }
            // extract localized message
            const list = path.split('.');
            const msg = list.reduce((acc, curr) => {
                if (hasProp(acc, curr)) {
                    return acc[curr];
                }
                return '';
            }, res);
            // default message?
            if (!msg) {
                return defaultMessage || `INTERNAL_ERROR: cannot find message with path = ${path}`;
            }
            // results
            return msg;
        };
        this.resources = resources;
        this.locale = locale;
        this.country = country;
    }
}

let MLOG_VERBOSE = true;
let ELOG_VERBOSE = true;
let ELOG_PREFIX = '[ERROR]';
// setMlog sets message logging verbose mode
const setMlog = (verbose) => (MLOG_VERBOSE = verbose);
// setElog sets error message logging verbose mode
const setElog = (verbose) => (ELOG_VERBOSE = verbose);
// setElogPrefix sets error log prefix
const setElogPrefix = (prefix) => (ELOG_PREFIX = prefix);
// mlog logs message, if verbose mode is on
const mlog = (message) => {
    if (MLOG_VERBOSE) {
        if (typeof message === 'string' ||
            typeof message === 'number' ||
            typeof message === 'boolean') {
            console.log(message);
        }
        else {
            console.log(JSON.stringify(message, undefined, 2));
        }
    }
};
// elog logs error, if vermobse mode is on, and returns the formatted error message
const elog = (error) => {
    let msg = '';
    if (error.message && typeof error.message === 'string') {
        error.message.replace(`${ELOG_PREFIX} `, '');
        msg = `${ELOG_PREFIX} ${error.message}`;
    }
    else {
        if (typeof error === 'string' || typeof error === 'number' || typeof error === 'boolean') {
            msg = `${ELOG_PREFIX} ${error}`;
        }
        else {
            msg = `${ELOG_PREFIX}\n${JSON.stringify(error, undefined, 2)}`;
        }
    }
    msg = msg.replace(`${ELOG_PREFIX} ${ELOG_PREFIX}`, ELOG_PREFIX);
    if (ELOG_VERBOSE) {
        console.log(msg);
    }
    return msg;
};

// makeGetField makes a getField function
const makeGetField = (obj, TT) => (fieldName) => {
    // check if field exists
    if (!Object.prototype.hasOwnProperty.call(obj, fieldName)) {
        throw new Error(`cannot find ${fieldName}`);
    }
    // check if type is correct
    const value = obj[fieldName];
    if (typeof value !== TT) {
        throw new Error(`type of ${fieldName} is incorrect`);
    }
    // results
    return value;
};

// makeSetFiled makes a setField function
const makeSetField = (obj, onChange, TT) => (fieldName, value) => {
    // check if field exists
    if (!Object.prototype.hasOwnProperty.call(obj, fieldName)) {
        throw new Error(`cannot find ${fieldName}`);
    }
    // previous value
    const oldValue = obj[fieldName];
    // check if type is correct
    if (typeof oldValue !== TT) {
        throw new Error(`type of ${fieldName} is incorrect`);
    }
    // check if value has been changed
    if (oldValue !== value) {
        obj[fieldName] = value;
        onChange();
    }
};

const appendToMapOfList = (a, key, item) => {
    if (a[key]) {
        a[key].push(item);
    }
    else {
        a[key] = [item];
    }
};
const mergeMapList = (a, b) => {
    const results = cloneSimple(a);
    Object.keys(b).forEach((key) => {
        if (results[key]) {
            results[key] = results[key].concat(b[key]);
        }
        else {
            results[key] = b[key];
        }
    });
    return results;
};

// maybeCopySimple (hierarchically) copies some data from origin
// into destination, iff the origin data exists (not undefined / not null)
// but it does copy empty strings and numbers
// NOTE: (1) will NOT recurse into array
//       (2) arrays must NOT have mixed types or objects;
//       (3) arrays must only contain "simple" types
//       (4) simple means:  string, number, boolean
//           i.e arrays must be "simple" as string[], number[], boolean[]
const maybeCopySimple = (destination, origin) => {
    for (const key of Object.keys(destination)) {
        if (hasProp(origin, key)) {
            const value = origin[key];
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    destination[key] = value.slice(0);
                }
                else if (typeof value === 'object') {
                    maybeCopySimple(destination[key], value);
                }
                else {
                    destination[key] = value;
                }
            }
        }
    }
};

const numOnly = (value) => {
    return value.replace(/[\D\s\._\-]+/g, '');
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const unixTimeNow = () => Math.floor(Date.now() / 1000);

export { Locales, allFilled, appendToMapOfList, blank2empty, camelize, cloneSimple, copySimple, diffSimple, elog, email2key, empty2blank, filled, getObjectKeys, hasProp, limitNumDecimals, makeGetField, makeSetField, maybeCopySimple, mergeMapList, mlog, numOnly, setElog, setElogPrefix, setMlog, shallowCompareArrays, sleep, unixTimeNow };
